if (!customElements.get('nelko-product-gallery')) {
  customElements.define(
    'nelko-product-gallery',
    class NelkoProductGallery extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'))
        this.panels = Array.from(this.querySelectorAll('[data-gallery-index]'))
        this.track = this.querySelector('[data-gallery-track]')
        this.status = this.querySelector('[data-gallery-status]')
        this.stage = this.querySelector('.nelko-product-gallery__stage')
        this.navRoot = this.querySelector('[data-gallery-nav]')
        this.prevZone = this.querySelector('[data-gallery-prev]')
        this.nextZone = this.querySelector('[data-gallery-next]')
        this.navCursor = this.querySelector('[data-gallery-cursor]')
        this.desktopNavMedia = window.matchMedia('(min-width: 1024px)')
        this.sectionId = this.dataset.sectionId
        this.colorOptionPosition = parseInt(this.dataset.colorOptionPosition || '0', 10)
        this.currentIndex = 0
        this.isSyncingGallery = false

        this.onTabClick = this.onTabClick.bind(this)
        this.onTabKeydown = this.onTabKeydown.bind(this)
        this.onTouchStart = this.onTouchStart.bind(this)
        this.onTouchEnd = this.onTouchEnd.bind(this)
        this.onSwatchClick = this.onSwatchClick.bind(this)
        this.onVariantChange = this.onVariantChange.bind(this)
        this.onPrevZoneClick = this.onPrevZoneClick.bind(this)
        this.onNextZoneClick = this.onNextZoneClick.bind(this)
        this.onNavZoneMove = this.onNavZoneMove.bind(this)
        this.onNavLeave = this.onNavLeave.bind(this)
        this.onDesktopNavChange = this.onDesktopNavChange.bind(this)

        this.touchStartX = 0
        this.touchStartY = 0

        if (this.panels.length === 0) return

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick)
          tab.addEventListener('keydown', this.onTabKeydown)
        })

        if (this.stage) {
          this.stage.addEventListener('touchstart', this.onTouchStart, { passive: true })
          this.stage.addEventListener('touchend', this.onTouchEnd, { passive: true })
        }

        this.bindColorSwatches()
        this.bindDesktopNav()

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange
          )
        }

        this.selectTab(this.getInitialIndex(), false, { syncVariant: false })
      }

      disconnectedCallback() {
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.onTabClick)
          tab.removeEventListener('keydown', this.onTabKeydown)
        })

        if (this.stage) {
          this.stage.removeEventListener('touchstart', this.onTouchStart)
          this.stage.removeEventListener('touchend', this.onTouchEnd)
        }

        this.unbindColorSwatches()
        this.unbindDesktopNav()
        this.variantChangeUnsubscriber?.()
      }

      get productInfo() {
        return this.closest('product-info')
      }

      get variantSelects() {
        return this.productInfo?.querySelector('variant-selects')
      }

      getInitialIndex() {
        const initialMediaId = this.dataset.initialMediaId
        if (initialMediaId) {
          const matchedIndex = this.panels.findIndex(
            (panel) => panel.dataset.mediaId === initialMediaId
          )
          if (matchedIndex >= 0) return matchedIndex
        }

        const selectedVariantId = new URLSearchParams(window.location.search).get('variant')
        if (!selectedVariantId) return 0

        const matchedIndex = this.panels.findIndex(
          (panel) => panel.dataset.variantId === selectedVariantId
        )

        return matchedIndex >= 0 ? matchedIndex : 0
      }

      bindDesktopNav() {
        if (!this.navRoot || this.panels.length <= 1) return

        this.prevZone?.addEventListener('click', this.onPrevZoneClick)
        this.nextZone?.addEventListener('click', this.onNextZoneClick)
        this.prevZone?.addEventListener('mousemove', this.onNavZoneMove)
        this.nextZone?.addEventListener('mousemove', this.onNavZoneMove)
        this.prevZone?.addEventListener('mouseenter', this.onNavZoneMove)
        this.nextZone?.addEventListener('mouseenter', this.onNavZoneMove)
        this.navRoot.addEventListener('mouseleave', this.onNavLeave)

        if (typeof this.desktopNavMedia.addEventListener === 'function') {
          this.desktopNavMedia.addEventListener('change', this.onDesktopNavChange)
        } else if (typeof this.desktopNavMedia.addListener === 'function') {
          this.desktopNavMedia.addListener(this.onDesktopNavChange)
        }
      }

      unbindDesktopNav() {
        this.prevZone?.removeEventListener('click', this.onPrevZoneClick)
        this.nextZone?.removeEventListener('click', this.onNextZoneClick)
        this.prevZone?.removeEventListener('mousemove', this.onNavZoneMove)
        this.nextZone?.removeEventListener('mousemove', this.onNavZoneMove)
        this.prevZone?.removeEventListener('mouseenter', this.onNavZoneMove)
        this.nextZone?.removeEventListener('mouseenter', this.onNavZoneMove)
        this.navRoot?.removeEventListener('mouseleave', this.onNavLeave)

        if (typeof this.desktopNavMedia.removeEventListener === 'function') {
          this.desktopNavMedia.removeEventListener('change', this.onDesktopNavChange)
        } else if (typeof this.desktopNavMedia.removeListener === 'function') {
          this.desktopNavMedia.removeListener(this.onDesktopNavChange)
        }

        this.hideNavCursor()
      }

      onDesktopNavChange() {
        if (!this.desktopNavMedia.matches) {
          this.hideNavCursor()
        }
      }

      isDesktopNavEnabled() {
        return this.desktopNavMedia.matches && this.panels.length > 1
      }

      getGalleryReferenceRect() {
        const panel = this.panels[this.currentIndex]
        const media = panel?.querySelector(
          '.nelko-product-gallery__image, .nelko-product-gallery__video, .nelko-product-gallery__model, .nelko-product-gallery__placeholder'
        )

        if (media) {
          return media.getBoundingClientRect()
        }

        return this.stage?.getBoundingClientRect()
      }

      getCursorScale(clientX) {
        const referenceRect = this.getGalleryReferenceRect()
        if (!referenceRect?.width) return 40

        const centerX = referenceRect.left + referenceRect.width / 2
        const halfWidth = referenceRect.width / 2
        const distanceFromCenter = Math.abs(clientX - centerX)
        const ratio = Math.max(0, Math.min(1, 1 - distanceFromCenter / halfWidth))
        const minSize = 32
        const maxSize = 56

        return minSize + ratio * (maxSize - minSize)
      }

      onPrevZoneClick(event) {
        if (!this.isDesktopNavEnabled()) return
        event.preventDefault()
        this.goToPrevious()
      }

      onNextZoneClick(event) {
        if (!this.isDesktopNavEnabled()) return
        event.preventDefault()
        this.goToNext()
      }

      onNavZoneMove(event) {
        if (!this.isDesktopNavEnabled() || !this.navCursor || !this.stage) return

        const zone = event.currentTarget
        const direction = zone === this.prevZone ? 'prev' : 'next'
        this.navCursor.classList.toggle('is-prev', direction === 'prev')

        const stageRect = this.stage.getBoundingClientRect()
        const x = event.clientX - stageRect.left
        const y = event.clientY - stageRect.top
        const size = this.getCursorScale(event.clientX)

        this.navCursor.style.left = `${x}px`
        this.navCursor.style.top = `${y}px`
        this.navCursor.style.width = `${size}px`
        this.navCursor.style.height = `${size}px`
        this.navCursor.hidden = false
        this.navCursor.classList.add('is-visible')
      }

      onNavLeave() {
        this.hideNavCursor()
      }

      hideNavCursor() {
        if (!this.navCursor) return
        this.navCursor.classList.remove('is-visible')
        this.navCursor.hidden = true
      }

      goToPrevious() {
        if (this.panels.length <= 1) return
        const nextIndex = (this.currentIndex - 1 + this.panels.length) % this.panels.length
        this.selectTab(nextIndex)
      }

      goToNext() {
        if (this.panels.length <= 1) return
        const nextIndex = (this.currentIndex + 1) % this.panels.length
        this.selectTab(nextIndex)
      }

      bindColorSwatches() {
        const swatchRoot = document.querySelector(`[data-gallery-swatches="${this.id}"]`)
        if (!swatchRoot) return

        this.swatchRoot = swatchRoot
        this.colorLabel = swatchRoot.querySelector('[data-selected-color]')
        this.colorSwatches = Array.from(swatchRoot.querySelectorAll('[data-option-value]'))

        if (!this.colorOptionPosition) {
          this.colorOptionPosition = parseInt(swatchRoot.dataset.colorOptionPosition || '0', 10)
        }

        this.colorSwatches.forEach((swatch) => {
          swatch.addEventListener('click', this.onSwatchClick)
        })
      }

      unbindColorSwatches() {
        this.colorSwatches?.forEach((swatch) => {
          swatch.removeEventListener('click', this.onSwatchClick)
        })
      }

      onSwatchClick(event) {
        const swatch = event.currentTarget
        if (swatch.disabled) return

        const colorValue = swatch.dataset.optionValue
        if (!colorValue) return

        this.selectColorOption(
          colorValue,
          parseInt(swatch.dataset.optionPosition || '0', 10),
          swatch
        )
      }

      onVariantChange(event) {
        if (event?.data?.sectionId !== this.sectionId) return
        if (this.isSyncingGallery) return

        const variant = event.data?.variant
        if (!variant) return

        this.isSyncingGallery = true
        try {
          const featuredMediaId = variant.featured_media?.id
          if (featuredMediaId) this.goToMediaId(featuredMediaId, { syncVariant: false })

          // Selection belongs to the variant, including variants without media
          // and different colors that share the same featured image.
          this.syncColorSwatches(this.currentIndex, variant.options?.[this.colorOptionPosition - 1])
        } finally {
          this.isSyncingGallery = false
        }
      }

      selectColorOption(colorValue, optionPosition, swatch) {
        const variantSelects = this.variantSelects

        if (variantSelects && optionPosition > 0) {
          const fieldset = variantSelects.querySelectorAll('fieldset')[optionPosition - 1]
          const matchingRadio = fieldset
            ? Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
                (input) => input.value === colorValue
              )
            : null

          if (matchingRadio) {
            this.syncColorSwatches(this.currentIndex, colorValue)
            if (!matchingRadio.checked) {
              matchingRadio.checked = true
              matchingRadio.dispatchEvent(new Event('change', { bubbles: true }))
            }
            return
          }
        }

        if (swatch?.dataset.mediaId) {
          this.goToMediaId(swatch.dataset.mediaId)
        }
      }

      goToMediaId(mediaId, options = {}) {
        const index = this.panels.findIndex((panel) => panel.dataset.mediaId === String(mediaId))
        if (index < 0) return
        this.selectTab(index, false, options)
      }

      syncColorSwatches(index, colorValue = this.panels[index]?.dataset.colorValue) {
        if (!this.colorSwatches?.length) return

        if (!colorValue) return

        this.colorSwatches.forEach((swatch) => {
          const isActive = swatch.dataset.optionValue === colorValue
          swatch.classList.toggle('is-active', isActive)
          swatch.setAttribute('aria-pressed', isActive ? 'true' : 'false')
        })

        if (this.colorLabel) {
          this.colorLabel.textContent = colorValue
        }

        const fieldset = this.variantSelects?.querySelectorAll('fieldset')[
          this.colorOptionPosition - 1
        ]
        const selectedValueNode = fieldset?.querySelector('[data-selected-value]')
        if (selectedValueNode) {
          selectedValueNode.textContent = colorValue
        }
      }

      syncVariantFromPanel(panel, options = {}) {
        if (options.syncVariant === false) return

        const colorValue = panel?.dataset.colorValue
        if (!colorValue || !this.colorOptionPosition) return

        const currentVariantId = this.getSelectedVariantId()
        if (currentVariantId && panel.dataset.variantId === currentVariantId) return

        this.selectColorOption(colorValue, this.colorOptionPosition)
      }

      getSelectedVariant() {
        const variantScript = this.variantSelects?.querySelector('[data-selected-variant]')
        if (!variantScript) return null

        try {
          return JSON.parse(variantScript.textContent)
        } catch (error) {
          return null
        }
      }

      getSelectedVariantId() {
        return this.getSelectedVariant()?.id?.toString() || new URLSearchParams(window.location.search).get('variant')
      }

      onTabClick(event) {
        const index = this.getTabIndex(event.currentTarget)
        if (index < 0) return
        this.selectTab(index)
      }

      onTabKeydown(event) {
        const currentIndex = this.getTabIndex(event.currentTarget)
        if (currentIndex < 0) return

        let nextIndex = currentIndex

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            nextIndex = (currentIndex + 1) % this.panels.length
            break
          case 'ArrowLeft':
          case 'ArrowUp':
            nextIndex = (currentIndex - 1 + this.panels.length) % this.panels.length
            break
          case 'Home':
            nextIndex = 0
            break
          case 'End':
            nextIndex = this.panels.length - 1
            break
          default:
            return
        }

        event.preventDefault()
        this.selectTab(nextIndex, true)
      }

      getTabIndex(tab) {
        const tabIndex = parseInt(tab.dataset.galleryTab, 10)
        return Number.isNaN(tabIndex) ? this.tabs.indexOf(tab) : tabIndex
      }

      onTouchStart(event) {
        const touch = event.changedTouches[0]
        this.touchStartX = touch.screenX
        this.touchStartY = touch.screenY
      }

      onTouchEnd(event) {
        if (this.panels.length <= 1) return

        const touch = event.changedTouches[0]
        const deltaX = touch.screenX - this.touchStartX
        const deltaY = touch.screenY - this.touchStartY

        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return

        const nextIndex =
          deltaX < 0
            ? (this.currentIndex + 1) % this.panels.length
            : (this.currentIndex - 1 + this.panels.length) % this.panels.length

        this.selectTab(nextIndex)
      }

      selectTab(index, focusTab, options = {}) {
        const safeIndex = Math.max(0, Math.min(index, this.panels.length - 1))
        this.currentIndex = safeIndex

        if (this.track) {
          this.track.style.transform = `translate3d(-${safeIndex * 100}%, 0, 0)`
        }

        this.tabs.forEach((tab, tabIndex) => {
          const selected = tabIndex === safeIndex
          tab.classList.toggle('is-active', selected)
          tab.setAttribute('aria-selected', selected ? 'true' : 'false')
          tab.tabIndex = selected ? 0 : -1
        })

        this.panels.forEach((panel, panelIndex) => {
          const selected = panelIndex === safeIndex
          panel.classList.toggle('is-active', selected)
          panel.setAttribute('aria-hidden', selected ? 'false' : 'true')
          if (!selected) this.pausePanelMedia(panel)
        })

        if (focusTab && this.tabs[safeIndex]) {
          this.tabs[safeIndex].focus()
        }

        const panel = this.panels[safeIndex]
        const colorValue = options.syncVariant === false
          ? this.getSelectedVariant()?.options?.[this.colorOptionPosition - 1]
          : panel?.dataset.colorValue
        this.syncColorSwatches(safeIndex, colorValue)

        if (!this.isSyncingGallery) {
          this.syncVariantFromPanel(panel, options)
        }

        this.announce(safeIndex)
      }

      pausePanelMedia(panel) {
        panel.querySelectorAll('video').forEach((video) => video.pause())
      }

      announce(index) {
        if (!this.status) return

        const tab = this.tabs[index]
        const panel = this.panels[index]
        const label =
          tab?.querySelector('.visually-hidden')?.textContent?.trim() ||
          panel?.dataset.colorValue ||
          tab?.getAttribute('aria-label') ||
          ''

        this.status.textContent = `${label}, ${index + 1} of ${this.panels.length}`
      }
    }
  )
}
